import React, { useMemo, useState } from 'react';
import { api } from '../lib/api';
import ConfirmDialog from './ConfirmDialog';
import { useLocale } from '../lib/LocaleContext';
import styles from './ConversationList.module.css';

export default function ConversationList({ data, loading, onRefresh, title }) {
  const { t } = useLocale();
  const [expandedCustomerIds, setExpandedCustomerIds] = useState(new Set());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingDeleteCustomerId, setPendingDeleteCustomerId] = useState(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState(null);
  const [detailMessage, setDetailMessage] = useState(null);

  const toggleCustomerGroup = id => setExpandedCustomerIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const groupConversationsByCustomer = convs => {
    const g = new Map();
    convs.forEach(c => { const k = c.customer_id || 'unknown'; if (!g.has(k)) g.set(k, []); g.get(k).push(c); });
    return Array.from(g.entries()).map(([cid, list]) => ({
      customerId: cid,
      conversations: [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    })).sort((a, b) => (a.customerId > b.customerId ? -1 : a.customerId < b.customerId ? 1 : 0));
  };

  const groupedData = useMemo(() => groupConversationsByCustomer(data), [data]);
  const handleRefresh = async () => {
    // prevent re-entry while previous refresh is in-flight
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeleteConversations = (customerId, e) => { e.stopPropagation(); setPendingDeleteCustomerId(customerId); setShowConfirmDialog(true); };
  const confirmDelete = async () => {
    if (!pendingDeleteCustomerId) return;
    const cid = pendingDeleteCustomerId;
    setShowConfirmDialog(false); setPendingDeleteCustomerId(null); setDeletingCustomerId(cid);
    try {
      const result = await api.deleteConversations(cid);
      if (result.success && onRefresh) await onRefresh();
      else alert(`${t.deleteFailed}${result.error || t.unknownError}`);
    } catch (e) { alert(t.deleteError); }
    finally { setDeletingCustomerId(null); }
  };
  const cancelDelete = () => { setShowConfirmDialog(false); setPendingDeleteCustomerId(null); };

  const formatDate = s => { try { return new Date(s).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
  const truncate = (t, n = 80) => (!t ? '-' : t.length <= n ? t : t.substring(0, n) + '...');

  return (
    <div className={styles.listContainer}>
      {(title || onRefresh) && (
        <div className={styles.listHeader}>
          {title && <h3>{title}</h3>}
          {onRefresh && (
            <button
              type="button"
              className={styles.refreshButton}
              onClick={handleRefresh}
              disabled={isRefreshing}
              title={t.refreshConversationList}
            >
              {isRefreshing ? t.refreshing : t.refresh}
            </button>
          )}
        </div>
      )}
      <div className={styles.listWrapper}>
        {loading && data.length === 0 ? <div className={styles.loadingState}><p>{t.loading}</p></div> : data.length === 0 ? <div className={styles.emptyState}><p>{t.noData}</p></div> : (
          <div className={styles.list}>
            {groupedData.map(g => {
              const expanded = expandedCustomerIds.has(g.customerId);
              return (
                <div key={g.customerId} className={styles.customerGroup}>
                  <div className={styles.customerGroupHeader} onClick={() => toggleCustomerGroup(g.customerId)}>
                    <div className={styles.customerGroupInfo}>
                      <span className={styles.customerId}>{t.customer}: {g.customerId?.substring(0, 20) || '-'}</span>
                      <span className={styles.conversationCount}>({g.conversations.length} {t.conversationCount})</span>
                      <button className={styles.deleteButton} onClick={e => handleDeleteConversations(g.customerId, e)} disabled={deletingCustomerId === g.customerId}>{t.delete}</button>
                    </div>
                  </div>
                  {expanded && (
                    <div className={styles.customerConversations}>
                      {g.conversations.map((c, idx) => (
                        <div key={c.id} className={styles.listItem}>
                          <div className={styles.listItemHeader} onClick={() => setDetailMessage(c)}>
                            <div className={styles.listItemInfo}>
                              <span className={styles.roundBadge}>{t.round.replace('{n}', idx + 1)}</span>
                              <span className={styles.timestamp}>{formatDate(c.created_at)}</span>
                              <div className={styles.messagePreview}><span className={styles.label}>{t.userLabel}</span> {truncate(c.user_message, 80)}</div>
                              <div className={styles.messagePreview}><span className={styles.label}>{t.aiLabel}</span> {truncate(c.ai_response, 80)}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ConfirmDialog
        show={showConfirmDialog && !!pendingDeleteCustomerId}
        title={t.confirmDelete}
        confirmText={t.delete}
        cancelText={t.cancel}
        confirmVariant="danger"
        loading={!!deletingCustomerId}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      >
        <p>{t.confirmDeleteConversations}</p>
      </ConfirmDialog>
      {detailMessage && (
        <div className={styles.detailOverlay} onClick={() => setDetailMessage(null)}>
          <div className={styles.detailModal} onClick={e => e.stopPropagation()}>
            <div className={styles.detailHeader}>
              <h3>{t.conversationDetail}</h3>
              <span className={styles.detailTime}>{formatDate(detailMessage.created_at)}</span>
              <button className={styles.detailClose} onClick={() => setDetailMessage(null)}>×</button>
            </div>
            <div className={styles.detailBody}>
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>{t.userMessage}</div>
                <div className={styles.detailContent}>{detailMessage.user_message || '-'}</div>
              </div>
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>{t.aiResponse}</div>
                <div className={styles.detailContent}>{detailMessage.ai_response || '-'}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
